import { Badge, Popover } from '@mui/material';
import React from 'react';
// import { ReactComponent as BellIcon } from 'src/assets/icons/bell-icon.svg';
import CloseIcon from 'src/assets/icons/fi-rr-cross-small.svg';
// import { ImportSpinner } from '../ImportV2/components/FileSelection';
import ImportIcon from 'src/assets/import/import-file-icon.svg';
import { useNavigate } from 'react-router-dom';
// import { ReactComponent as ExclamationIcon } from 'src/assets/icons/fi-sr-exclamation.svg';
// import { ReactComponent as CheckIcon } from 'src/assets/import/check-circle.svg';
// import { ReactComponent as PartialIcon } from 'src/assets/icons/fi-sr-info.svg';
import NotificationIcon from 'src/assets/navbar-icons/notification-icon.svg';
import NotificationIconActive from 'src/assets/navbar-icons/notification-icon-active.svg';
import cx from 'classnames';
import { useMultiJobStream } from 'src/hooks/useMultiJobStream';
import { apiClient } from 'src/api/apiClient';
import { useProfile } from 'src/hooks/useProfile';
import { GET_BULK_IMPORT_LOGS } from 'src/constants/httpEndPoints';
import { formatNumberToBytes } from 'src/utils/utilityMethods';
import NoNotificationsIcon from 'src/assets/icons/no-notifications-icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { setNewNotifications } from 'src/store/notifications/notifications.actions';
import EllipsisText from '../EllipsisText';
import { trackLast3Notifications } from 'src/mix-panel/notifications/functions';
import NavbarItem from '../Navbar/NavbarItem';
import LinkedInIcon from 'src/assets/icons/linkedin-v2.svg';
import { timezoneConverter } from 'src/utils/timezonesConvertor';
import dayjs from 'dayjs';

const Notifications = ({
  expanded,
  triggerCollapseNav,
  closePopoverRef,
  setIsPopoverOpen,
}: {
  expanded?: boolean;
  triggerCollapseNav?: () => void;
  closePopoverRef?: React.MutableRefObject<(() => void) | null>;
  setIsPopoverOpen?: (isOpen: boolean) => void;
}) => {
  const { newNotifications, refreshNotifications, startNotificationsApiPolling } = useSelector(
    (state: any) => state.notificationsState,
  );
  const [hasNewNotification, setHasNewNotification] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const [importLogsLoading, setImportLogsLoading] = React.useState(false);
  const navigate = useNavigate();
  const { customerId, emailId, clientName } = useProfile();
  const dispatch = useDispatch();
  const [isAnyNotifificationInProgress, setIsAnyNotificationInProgress] = React.useState(false);
  const closeTimeoutRef = React.useRef<number | null>(null);

  // Clear any pending close timeout
  const cancelCloseTimeout = React.useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);
  const userTimezone = useSelector((state: any) => state.user.userTimezone);
  React.useEffect(() => {
    if (refreshNotifications > 0) {
      fetchingImportLogs(true);
    }
  }, [refreshNotifications]);

  React.useEffect(() => {
    fetchingImportLogs();
  }, []);
  React.useEffect(() => {
    if (startNotificationsApiPolling) {
      setIsAnyNotificationInProgress(true);
    }
  }, [startNotificationsApiPolling]);

  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    cancelCloseTimeout(); // Cancel any pending close
    trackLast3Notifications(newNotifications, clientName);
    setAnchorEl(event.currentTarget);
    setHasNewNotification(false);
    setIsPopoverOpen && setIsPopoverOpen(true);
  };

  const navigateToImportLogs = (notification: any) => {
    navigate('/target-profiles/import-logs', {
      state: notification?.entityType === 'ACCOUNT' ? 'accounts' : 'contacts',
    });
    setAnchorEl(null);
    setIsPopoverOpen && setIsPopoverOpen(false);
  };

  const handleClose = React.useCallback(() => {
    cancelCloseTimeout(); // Cancel any pending delayed close
    setAnchorEl(null);
    setIsPopoverOpen && setIsPopoverOpen(false);
    triggerCollapseNav && triggerCollapseNav();
  }, [triggerCollapseNav, setIsPopoverOpen, cancelCloseTimeout]);

  // Delayed close for when mouse leaves popover
  const handlePopoverMouseLeave = React.useCallback(() => {
    // Add a small delay before closing to allow moving back to navbar
    closeTimeoutRef.current = window.setTimeout(() => {
      handleClose();
    }, 200); // 200ms delay
  }, [handleClose]);

  // Cancel close when mouse re-enters popover
  const handlePopoverMouseEnter = React.useCallback(() => {
    cancelCloseTimeout();
  }, [cancelCloseTimeout]);

  // Register the close function to the ref so parent can close the popover
  React.useEffect(() => {
    if (closePopoverRef) {
      closePopoverRef.current = handleClose;
    }
    return () => {
      if (closePopoverRef) {
        closePopoverRef.current = null;
      }
    };
  }, [closePopoverRef, handleClose]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      cancelCloseTimeout();
    };
  }, [cancelCloseTimeout]);

  const fetchingImportLogs = (force = false) => {
    if (importLogsLoading) return;
    if (!force && !startNotificationsApiPolling && !isAnyNotifificationInProgress) return;
    setImportLogsLoading(true);
    apiClient
      .get(`${GET_BULK_IMPORT_LOGS}?pageSize=${20}&pageNo=${0}&searchText=&entityType=ALL`, {
        headers: {
          'X-Login-Email': emailId,
          'X-Customer-Id': customerId,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          const currentResponse = response?.data?.list;
          if (newNotifications.length > 0) {
            currentResponse.forEach((currentItem: any) => {
              const previousItem = newNotifications.find((item: any) => item.id === currentItem.id);

              // Check if `isProcessComplete` has changed from `false` to `true`

              if (
                previousItem &&
                !previousItem.isProcessComplete &&
                currentItem.isProcessComplete
              ) {
                setHasNewNotification(true);
              }
            });
          }

          // Update the previous response for the next iteration
          dispatch(setNewNotifications(currentResponse));
          setIsAnyNotificationInProgress(() =>
            currentResponse.some((notification: any) => !notification.isProcessComplete),
          );
        }
      })
      .catch(() => {
        setImportLogsLoading(false);
        console.log('Something went wrong. Please try after sometime.');
      })
      .finally(() => setImportLogsLoading(false));
  };

  const renderFileStatus = (notificationStatus: any) => {
    const fileStatus = notificationStatus;

    const customIconStyles = cx({
      'import-status-icon': true,
      'import-completed-icon':
        fileStatus?.toLowerCase() === 'import completed' ||
        fileStatus?.toLowerCase() === 'fully imported',
      'failed-to-import-icon': fileStatus?.toLowerCase() === 'failed to import',
      'partial-import-completed-icon':
        fileStatus?.toLowerCase() === 'partial import completed' ||
        fileStatus?.toLowerCase() === 'partially imported',
    });
    const customStyles = cx({
      'notification-status-div': true,
      'import-completed':
        fileStatus?.toLowerCase() === 'import completed' ||
        fileStatus?.toLowerCase() === 'fully imported',
      'failed-to-import': fileStatus?.toLowerCase() === 'failed to import',
      'partial-import-completed':
        fileStatus?.toLowerCase() === 'partial import completed' ||
        fileStatus?.toLowerCase() === 'partially imported',
      'in-progress':
        fileStatus?.toLowerCase() === 'importing contacts' ||
        fileStatus?.toLowerCase() === 'importing accounts' ||
        fileStatus?.toLowerCase() === 'in progress',
    });

    // const statusIcon = (status: string) => {
    //   switch (status?.toLowerCase()) {
    //     case 'import completed':
    //       return <CheckIcon className={customIconStyles} />;
    //     case 'failed to import':
    //       return <ExclamationIcon className={customIconStyles} />;
    //     case 'partial import completed':
    //       return (
    //         <PartialIcon height={16} width={16} viewBox="0 0 16 16" className={customIconStyles} />
    //       );
    //     case 'importing contacts':
    //     case 'importing accounts':
    //       return <ImportSpinner height={24} width={24} />;
    //     default:
    //       return <></>;
    //   }
    // };

    return (
      <div className={customStyles}>
        {/* {statusIcon(notificationStatus)} */}
        {notificationStatus}
      </div>
    );
  };

  const inProgressNotificationIds = React.useMemo(
    () => newNotifications.filter((n: any) => !n.isProcessComplete).map((n: any) => String(n.id)),
    [newNotifications],
  );

  useMultiJobStream({
    type: 'IMPORT_LOGS',
    jobIds: inProgressNotificationIds,
    enabled: startNotificationsApiPolling && inProgressNotificationIds.length > 0,
    onData: (jobId, data) => {
      if (!data) return;
      const updated = data as any;
      dispatch(
        setNewNotifications(
          newNotifications.map((n: any) =>
            String(n.id) === jobId ? { ...n, ...updated } : n,
          ),
        ),
      );
      if (updated.isProcessComplete) setHasNewNotification(true);
    },
    onJobDone: () => {
      fetchingImportLogs(true);
      setIsAnyNotificationInProgress(
        newNotifications.some((n: any) => !n.isProcessComplete),
      );
    },
  });
  const isActive = Boolean(anchorEl);
  // const icon = isActive ? (
  //   <NotificationIconActive width="12" height="13" viewBox="0 0 12 13" />
  // ) : (
  //   <NotificationIcon width="12" height="13" viewBox="0 0 12 13" />
  // );
  const icon = <img src={isActive ? NotificationIconActive : NotificationIcon} alt={isActive ? 'notification-icon-active' : 'notification-icon'} width="16" height="16" />;

  return (
    <>
      <div onMouseEnter={cancelCloseTimeout} style={expanded ? { width: '100%' } : {}}>
        <NavbarItem
          expanded={Boolean(expanded)}
          icon={
            <Badge
              variant="dot"
              color="success"
              className="notification-online-status"
              invisible={!hasNewNotification}
            >
              {icon}
            </Badge>
          }
          title="Notifications"
          permission={true}
          onClick={handleClick}
          isActive={isActive}
          isLocked={false}
        />
      </div>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          marginLeft: '16px',
        }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
          },
        }}
      >
        <div className="notifications-dropdown-wrapper">
          <div className="notifications-header">
            <div className="header-label">Notifications</div>

            <img src={CloseIcon} alt="close-icon" width="16" height="16" onClick={handleClose} className="close-icon" />
          </div>
          <div className="notifications-content">
            {newNotifications?.length > 0 ? (
              (newNotifications?.length > 3 ? newNotifications.slice(0, 3) : newNotifications).map(
                (notification: any) => {
                  const formattedDate = dayjs(
                    timezoneConverter(notification?.createdDate, userTimezone, true),
                    'DD MMM YYYY, HH:mm',
                  ).format('MM/DD/YYYY hh:mm A');
                  return (
                    <div
                      key={notification?.id}
                      className="notification-card"
                      onClick={() => navigateToImportLogs(notification)}
                    >
                      <div className="icon-div">
                        <img src={notification?.type === 'LIST' ? LinkedInIcon : ImportIcon} alt={notification?.type === 'LIST' ? 'linkedin-icon' : 'import-icon'} />
                      </div>
                      <div className="content">
                        <div className="notification-name">
                          {notification?.fileName?.length > 30 ? (
                            <EllipsisText text={notification?.fileName} />
                          ) : (
                            notification?.fileName
                          )}
                        </div>
                        <div className="notification-status-wrapper">
                          <div className="file-size-label">
                            {notification?.type !== 'LIST'
                              ? formatNumberToBytes(notification?.size)
                              : notification?.createdDate
                              ? formattedDate
                              : 'N/A'}
                          </div>
                          <span className="dot-label">.</span>
                          {renderFileStatus(notification?.status)}
                        </div>
                      </div>
                    </div>
                  );
                },
              )
            ) : (
              <div className="no-notifications-wrapper">
                <img src={NoNotificationsIcon} alt="no-notifications-icon" />
                <div>No notifications yet</div>
              </div>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default Notifications;
